import { useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'
import type { Drawing, Question, Profile, Tag } from '../types/database'
import { Sidebar } from './Sidebar'
import { DrawingDetail } from './DrawingDetail'
import { DrawingCanvas } from './DrawingCanvas'
import { useAuth } from '../contexts/AuthContext'
import { getFileUrl } from '../utils/fileUtils'
import html2canvas from 'html2canvas'

type DrawingWithQuestion = Drawing & {
  question?: Question | null
  user?: Profile | null
  tags?: Tag[]
}

type StatusFilter = 'default' | 'finished' | 'live' | 'all'

const formatDuration = (seconds: number | null) => {
  if (seconds === null) return '-'
  const m = Math.floor(seconds / 60)
  const s = Math.round(seconds % 60)
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
}

export function DrawingList() {
  const { session, roles } = useAuth()

  // Check if user can delete a specific drawing
  const canDeleteDrawing = () => {
    if (!session) return false
    if (roles.includes('admin') || roles.includes('moderator')) return true;
    return false;
  }
  const [drawings, setDrawings] = useState<DrawingWithQuestion[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedDrawingId, setSelectedDrawingId] = useState<number | null>(null)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('default')

  // Tag and user filter state
  const [availableTags, setAvailableTags] = useState<Tag[]>([])
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([])
  const [availableUsers, setAvailableUsers] = useState<Profile[]>([])
  const [selectedUserId, setSelectedUserId] = useState<string>('all')

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(20)
  const [totalCount, setTotalCount] = useState(0)

  // Debounce ref for search query
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Batch Download State
  const [selectedDrawingIds, setSelectedDrawingIds] = useState<number[]>([])
  const [isBatchProcessing, setIsBatchProcessing] = useState(false)
  const [batchQueue, setBatchQueue] = useState<DrawingWithQuestion[]>([])
  const [currentBatchItem, setCurrentBatchItem] = useState<DrawingWithQuestion | null>(null)
  const [currentBatchPdfUrl, setCurrentBatchPdfUrl] = useState<string | null>(null)
  const [hiddenGrayscaleMode, setHiddenGrayscaleMode] = useState(true)

  // Batch Processor
  useEffect(() => {
    if (!isBatchProcessing) return
    if (currentBatchItem) return // Busy

    if (batchQueue.length === 0) {
      setIsBatchProcessing(false)
      alert('일괄 다운로드가 완료되었습니다.')
      return
    }

    const next = batchQueue[0]
    console.log('[Batch] Processing next item:', next.id)
    setCurrentBatchItem(next)

    // Fetch PDF for next
    const fetchPdf = async () => {
      if (!next.question?.file) {
        console.log('[Batch] No PDF for item:', next.id)
        setCurrentBatchPdfUrl(null)
        return
      }
      try {
        // @ts-ignore
        const bookCode = next.question.question_groups?.books?.code
        const url = await getFileUrl(next.question.file, bookCode)
        console.log('[Batch] PDF URL fetched:', url)
        setCurrentBatchPdfUrl(url)
      } catch (e) {
        console.error('[Batch] PDF fetch failed:', e)
        setCurrentBatchPdfUrl(null)
      }
    }
    fetchPdf()
  }, [isBatchProcessing, batchQueue, currentBatchItem])

  const handleCanvasReady = async () => {
    console.log('[Batch] handleCanvasReady called for item:', currentBatchItem?.id)
    if (!currentBatchItem) return

    // Capture
    // We target the INNER ID 'drawing-canvas-0' generated by DrawingCanvas
    // This avoids capturing the 'hidden-batch-canvas-0' wrapper which includes buttons and Tailwind bg-white (oklch)
    const element = document.getElementById('drawing-canvas-0') // DrawingCanvas uses id="drawing-canvas-0"
    // Wait, DrawingCanvas dynamically sets ID based on index.
    // In Hidden Renderer, we pass drawingData which likely has 1 page (index 0).
    // So 'drawing-canvas-0' should be correct if DrawingCanvas renders page 0.

    if (element) {
      console.log('[Batch] Element found, starting capture')
      try {
        const canvas = await html2canvas(element, {
          useCORS: true,
          allowTaint: true,
          scale: 2,
          logging: true, // Enable html2canvas logging
          windowWidth: 1200, // Force dimensions
          windowHeight: 1200
        } as any)

        console.log('[Batch] Capture successful, URL length:', canvas.toDataURL('image/png').length)

        const link = document.createElement('a')
        const name = currentBatchItem.question?.name || 'unnamed'
        link.download = `drawing-${currentBatchItem.id}-${name}-grayscale.png`
        link.href = canvas.toDataURL('image/png')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (e) {
        console.error('[Batch] Capture failed', e)
      }
    } else {
      console.error('[Batch] Element not found!')
    }

    // Move next (short delay to ensure cleanup/state update)
    setTimeout(() => {
      console.log('[Batch] Moving to next item')
      setBatchQueue(prev => prev.slice(1))
      setCurrentBatchItem(null)
      setCurrentBatchPdfUrl(null)
    }, 100)
  }

  const fetchDrawings = async (params: {
    searchQuery: string
    statusFilter: StatusFilter
    selectedUserId: string
    selectedTagIds: number[]
    currentPage: number
    itemsPerPage: number
  }) => {
    try {
      setLoading(true)

      // Step 1: Get filtered IDs + total count via search_drawings RPC
      const { data: searchResult, error: searchError } = await supabase.rpc('search_drawings', {
        p_search_query: params.searchQuery,
        p_status_filter: params.statusFilter,
        p_user_id: params.selectedUserId,
        p_tag_ids: params.selectedTagIds,
        p_page: params.currentPage,
        p_page_size: params.itemsPerPage,
      })

      if (searchError) throw searchError

      const resultRows = (searchResult || []) as { id: number; total_count: number }[]
      const count = resultRows.length > 0 ? Number(resultRows[0].total_count) : 0
      setTotalCount(count)

      if (resultRows.length === 0) {
        setDrawings([])
        return
      }

      // Step 2: Fetch full drawing data for the returned IDs
      const ids = resultRows.map((r) => r.id)
      const { data, error } = await supabase
        .from('drawings')
        .select(`
          *,
          question:questions(
            *,
            question_groups(
              books(code)
            )
          ),
          user:profiles(*)
        `)
        .in('id', ids)
        .order('created_at', { ascending: false })

      if (error) throw error

      // Fetch tags for each drawing
      let drawingsWithTags: DrawingWithQuestion[] = (data || []) as DrawingWithQuestion[]

      if (data && data.length > 0) {
        const drawingIds = data.map((d) => d.id)
        const { data: drawingTagsData } = await supabase
          .from('drawing_tags')
          .select('drawing_id, tags(*)')
          .in('drawing_id', drawingIds)

        drawingsWithTags = data.map((drawing) => {
          const drawingTags = (drawingTagsData || [])
            .filter((dt: any) => dt.drawing_id === drawing.id)
            .map((dt: any) => dt.tags)
            .filter(Boolean) as Tag[]
          return { ...drawing, tags: drawingTags }
        }) as DrawingWithQuestion[]
      }

      setDrawings(drawingsWithTags)

      // Trigger duration update for drawings with missing duration
      const drawingsToUpdate = drawingsWithTags.filter(d => d.duration === null && d.drawing_data)
      if (drawingsToUpdate.length > 0) {
        console.log(`[Duration] Found ${drawingsToUpdate.length} drawings with missing duration. Triggering update...`)
        Promise.allSettled(
          drawingsToUpdate.map(d =>
            supabase.functions.invoke('update-duration', {
              body: { drawingId: d.id }
            })
          )
        ).then(() => {
          console.log('[Duration] Update triggers sent.')
        })
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch drawings')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        // Fetch available tags
        const { data: tagsData, error: tagsError } = await supabase
          .from('tags')
          .select('*')
          .order('tag_type', { ascending: true })
          .order('name', { ascending: true })

        if (tagsError) throw tagsError
        setAvailableTags(tagsData || [])

        // Fetch available users who have drawings
        const { data: usersData, error: usersError } = await supabase
          .from('profiles')
          .select('*')
          .order('last_name', { ascending: true })
          .order('first_name', { ascending: true })

        if (usersError) throw usersError
        setAvailableUsers(usersData || [])
      } catch (err) {
        console.error('Failed to fetch filters:', err)
      }
    }

    fetchFilters()
  }, [])

  // Re-fetch when filters or pagination change (with debounce for searchQuery)
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)

    searchDebounceRef.current = setTimeout(() => {
      fetchDrawings({ searchQuery, statusFilter, selectedUserId, selectedTagIds, currentPage, itemsPerPage })
    }, 300)

    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
    }
  }, [searchQuery, statusFilter, selectedUserId, selectedTagIds, currentPage, itemsPerPage])

  // Reset to first page when filters change (except page/pageSize itself)
  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery, statusFilter, selectedUserId, selectedTagIds])

  const handleDelete = async (id: number) => {
    if (!confirm('정말 삭제하시겠습니까?')) return

    try {
      const { error } = await supabase
        .from('drawings')
        .delete()
        .eq('id', id)

      if (error) throw error
      fetchDrawings({ searchQuery, statusFilter, selectedUserId, selectedTagIds, currentPage, itemsPerPage })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete drawing')
    }
  }

  const handleBackToList = () => {
    setSelectedDrawingId(null)
    fetchDrawings({ searchQuery, statusFilter, selectedUserId, selectedTagIds, currentPage, itemsPerPage })
  }

  const handleToggleSelect = (id: number) => {
    setSelectedDrawingIds(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    )
  }

  const handleSelectAll = () => {
    const visibleIds = drawings.map(d => d.id)
    const allSelected = visibleIds.length > 0 && visibleIds.every(id => selectedDrawingIds.includes(id))

    if (allSelected) {
      setSelectedDrawingIds(prev => prev.filter(id => !visibleIds.includes(id)))
    } else {
      const newIds = [...selectedDrawingIds]
      visibleIds.forEach(id => {
        if (!newIds.includes(id)) newIds.push(id)
      })
      setSelectedDrawingIds(newIds)
    }
  }

  const handleBatchDownloadStart = async () => {
    if (selectedDrawingIds.length === 0) return
    if (!confirm(`${selectedDrawingIds.length}개의 항목을 Grayscale PNG로 다운로드 하시겠습니까?`)) return

    // Fetch selected drawings (may span multiple pages)
    const { data } = await supabase
      .from('drawings')
      .select(`
        *,
        question:questions(
          *,
          question_groups(
            books(code)
          )
        ),
        user:profiles(*)
      `)
      .in('id', selectedDrawingIds)
      .order('created_at', { ascending: false })

    setBatchQueue((data || []) as DrawingWithQuestion[])
    setIsBatchProcessing(true)
  }

  if (selectedDrawingId !== null) {
    return <DrawingDetail drawingId={selectedDrawingId} onBack={handleBackToList} />
  }

  if (loading) {
    return (
      <div className="relative flex min-h-screen w-full bg-background-light font-display">
        <Sidebar />
        <main className="ml-64 flex flex-1 items-center justify-center">
          <div className="flex items-center gap-2 text-gray-600">
            <span className="material-symbols-outlined animate-spin">progress_activity</span>
            <span className="text-lg">로딩 중...</span>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="relative flex min-h-screen w-full bg-background-light font-display">
      <Sidebar />
      <main className="ml-64 flex flex-1 flex-col p-6 lg:p-8">
        <div className="w-full max-w-7xl mx-auto">
          <header className="flex flex-wrap justify-between items-center gap-4 mb-6">
            <h1 className="text-3xl font-bold leading-tight tracking-tight text-gray-900">문제 풀이</h1>
            {selectedDrawingIds.length > 0 && (
              <button
                onClick={handleBatchDownloadStart}
                disabled={isBatchProcessing}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-amber-700 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors disabled:opacity-50"
              >
                {isBatchProcessing ? (
                  <>
                    <span className="material-symbols-outlined animate-spin text-base">progress_activity</span>
                    <span>처리 중... ({batchQueue.length}개 남음)</span>
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined text-base">palette</span>
                    <span>선택 항목 다운로드 ({selectedDrawingIds.length})</span>
                  </>
                )}
              </button>
            )}
          </header>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
              <span className="material-symbols-outlined text-base">error</span>
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Filter Box - Contains all filters */}
          <div className="mb-6 p-4 bg-white border border-gray-200 rounded-lg">
            <div className="space-y-3">
              {/* Row 1: Search and Status Filters */}
              <div className="flex flex-wrap gap-3">
                <div className="flex w-full flex-1 items-stretch rounded-lg h-10 bg-white border border-gray-200 focus-within:ring-2 focus-within:ring-primary lg:w-auto lg:flex-1">
                  <div className="text-gray-400 flex items-center justify-center pl-3">
                    <span className="material-symbols-outlined">search</span>
                  </div>
                  <input
                    className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-r-lg text-gray-900 focus:outline-0 focus:ring-0 border-none bg-transparent h-full placeholder:text-gray-400 px-4 pl-2 text-sm"
                    placeholder="문제 이름으로 검색..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>

                {/* Status filters */}
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">상태:</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setStatusFilter('default')}
                      className={`inline-flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${statusFilter === 'default'
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                      기본
                    </button>
                    <button
                      onClick={() => setStatusFilter('finished')}
                      className={`inline-flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${statusFilter === 'finished'
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                      완료
                    </button>
                    <button
                      onClick={() => setStatusFilter('live')}
                      className={`inline-flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${statusFilter === 'live'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                      라이브
                    </button>
                    <button
                      onClick={() => setStatusFilter('all')}
                      className={`inline-flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${statusFilter === 'all'
                        ? 'bg-gray-800 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                      모두
                    </button>
                  </div>
                </div>
              </div>

              {/* Row 2: User and Tag Filters */}
              <div className="flex flex-wrap gap-4">
                {/* User filter */}
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">사용자:</label>
                  <select
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="all">전체</option>
                    {availableUsers.map((user) => (
                      <option key={user.id} value={user.id}>
                        {[user.last_name, user.first_name].filter(Boolean).join(' ') || user.email}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Tag filter */}
                <div className="flex-1 flex flex-wrap items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">태그:</label>
                  <div className="flex flex-wrap gap-2">
                    {availableTags.map((tag) => {
                      const isSelected = selectedTagIds.includes(tag.id)
                      return (
                        <button
                          key={tag.id}
                          onClick={() => {
                            if (isSelected) {
                              setSelectedTagIds(selectedTagIds.filter(id => id !== tag.id))
                            } else {
                              setSelectedTagIds([...selectedTagIds, tag.id])
                            }
                          }}
                          className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${isSelected
                            ? 'text-white ring-2 ring-offset-1'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          style={isSelected ? { backgroundColor: tag.color } : undefined}
                        >
                          {isSelected && (
                            <span className="material-symbols-outlined text-sm">check</span>
                          )}
                          <span>{tag.name}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
            {drawings.length === 0 ? (
              <div className="text-center py-12">
                <span className="material-symbols-outlined text-gray-300 text-6xl mb-4">inbox</span>
                <p className="text-gray-500">
                  {searchQuery ? '검색 결과가 없습니다.' : '등록된 솔루션이 없습니다.'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-max">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 w-10">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-primary focus:ring-primary"
                          checked={drawings.length > 0 && drawings.every(d => selectedDrawingIds.includes(d.id))}
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        id
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        문제
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        사용자
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        태그
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        소요시간
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        등록일시
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        상태
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        작업
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {drawings.map((drawing) => (
                      <tr key={drawing.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-2">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 text-primary focus:ring-primary"
                            checked={selectedDrawingIds.includes(drawing.id)}
                            onChange={() => handleToggleSelect(drawing.id)}
                          />
                        </td>
                        <td className="h-[72px] px-4 py-2 text-sm text-gray-500">
                          {drawing.id}
                        </td>
                        <td className="h-[72px] px-4 py-2 text-sm font-medium text-gray-800">
                          {drawing.question?.name || '-'}
                        </td>
                        <td className="h-[72px] px-4 py-2 text-sm text-gray-500">
                          {drawing.user ? [drawing.user.last_name, drawing.user.first_name].filter(Boolean).join(' ') || '-' : '-'}
                        </td>
                        <td className="h-[72px] px-4 py-2">
                          {/* Tags display */}
                          {drawing.tags && drawing.tags.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {drawing.tags.map((tag) => (
                                <span
                                  key={tag.id}
                                  className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full text-white"
                                  style={{ backgroundColor: tag.color }}
                                >
                                  {tag.name}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs">-</span>
                          )}
                        </td>
                        <td className="h-[72px] px-4 py-2 text-sm font-medium">
                          <span className={`${(drawing.duration || 0) >= 180 ? 'text-amber-600 font-bold' : 'text-gray-500'}`}>
                            {formatDuration(drawing.duration)}
                          </span>
                        </td>
                        <td className="h-[72px] px-4 py-2 text-sm text-gray-500">
                          {new Date(drawing.created_at).toLocaleString('ko-KR', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </td>
                        <td className="h-[72px] px-4 py-2">
                          {drawing.status === 400 ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                              <span className="material-symbols-outlined text-sm">check_circle</span>
                              <span>완료</span>
                            </span>
                          ) : drawing.status === 200 ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded-full">
                              <span className="material-symbols-outlined text-sm">sensors</span>
                              <span>라이브</span>
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-yellow-700 bg-yellow-100 rounded-full">
                              <span className="material-symbols-outlined text-sm">play_circle</span>
                              <span>시작됨</span>
                            </span>
                          )}
                        </td>
                        <td className="h-[72px] px-4 py-2">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setSelectedDrawingId(drawing.id)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                            >
                              <span className="material-symbols-outlined text-sm">visibility</span>
                              <span>보기</span>
                            </button>
                            {canDeleteDrawing() && (
                              <button
                                onClick={() => handleDelete(drawing.id)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                              >
                                <span className="material-symbols-outlined text-sm">delete</span>
                                <span>삭제</span>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination Controls */}
          {totalCount > 0 && (
            <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              {/* Results summary */}
              <div className="text-sm text-gray-600">
                전체 {totalCount}개 중 {Math.min((currentPage - 1) * itemsPerPage + 1, totalCount)}-
                {Math.min(currentPage * itemsPerPage, totalCount)}개 표시
              </div>

              <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                {/* Items per page selector */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">페이지당:</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value))
                      setCurrentPage(1) // Reset to first page when changing items per page
                    }}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value={10}>10개</option>
                    <option value={20}>20개</option>
                    <option value={50}>50개</option>
                    <option value={100}>100개</option>
                  </select>
                </div>

                {/* Page navigation */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white"
                  >
                    <span className="material-symbols-outlined text-sm">chevron_left</span>
                    <span>이전</span>
                  </button>

                  <div className="flex items-center gap-1">
                    {(() => {
                      const totalPages = Math.ceil(totalCount / itemsPerPage)
                      const pages: (number | string)[] = []

                      if (totalPages <= 7) {
                        for (let i = 1; i <= totalPages; i++) {
                          pages.push(i)
                        }
                      } else {
                        if (currentPage <= 3) {
                          pages.push(1, 2, 3, 4, '...', totalPages)
                        } else if (currentPage >= totalPages - 2) {
                          pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages)
                        } else {
                          pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages)
                        }
                      }

                      return pages.map((page, idx) => (
                        page === '...' ? (
                          <span key={`ellipsis-${idx}`} className="px-2 text-gray-500">...</span>
                        ) : (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page as number)}
                            className={`min-w-[32px] rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${currentPage === page
                              ? 'bg-primary text-white'
                              : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                              }`}
                          >
                            {page}
                          </button>
                        )
                      ))
                    })()}
                  </div>

                  <button
                    onClick={() => setCurrentPage(Math.min(Math.ceil(totalCount / itemsPerPage), currentPage + 1))}
                    disabled={currentPage >= Math.ceil(totalCount / itemsPerPage)}
                    className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white"
                  >
                    <span>다음</span>
                    <span className="material-symbols-outlined text-sm">chevron_right</span>
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Hidden Batch Renderer - "Visible" to DOM but off-screen */}
        <div style={{ position: 'fixed', left: '200vw', top: 0, zIndex: -1000, width: '1200px', height: '1200px', overflow: 'hidden' }}>
          {currentBatchItem && (
            <div id="hidden-batch-canvas-0">
              <DrawingCanvas
                key={currentBatchItem.id}
                drawingData={currentBatchItem.drawing_data}
                pdfUrl={currentBatchPdfUrl}
                isGrayscaleMode={hiddenGrayscaleMode}
                setIsGrayscaleMode={setHiddenGrayscaleMode}
                onCanvasReady={handleCanvasReady}
              />
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
